export type PortalShellModuleAccess = {
  moduleKey: string;
  userHasVisibleAccess?: boolean;
  userHasOperationalAccess: boolean;
  visibilityState?: "hidden" | "visible" | "operable";
  scope: string;
  reasons: string[];
  visibilityReasons?: string[];
  operationalReasons?: string[];
  channelHasService?: boolean;
  contractIds?: number[];
  responsibleChannelIds?: number[];
};

export type PortalShellService = {
  moduleKey: string;
  companyHasService: boolean;
  channelHasService?: boolean;
  contractLabel: string;
  serviceKey?: string;
  serviceName?: string;
  contractIds?: number[];
  responsibleChannelIds?: number[];
};

export type PortalShellAIEntitlements = {
  hasAIWidget: boolean;
  hasAIInsights: boolean;
  contractIds?: number[];
  responsibleChannelIds?: number[];
  sourceServiceKeys?: string[];
};

export type PortalContext = "portal" | "nucleo" | "nr1";
export type PortalSidebarMode = PortalContext | "blocked";

export type NavigationTreeItem = {
  label: string;
  href?: string;
  children?: NavigationTreeItem[];
  sectionId?: string;
  moduleKey?: string;
};

export type PortalServiceSection = {
  id: string;
  label: string;
  description: string;
  adminOnly?: boolean;
};

const PORTAL_SERVICE_ROUTE_MAP = {
  telemedicina: "/portal/servicos/telemedicina",
  aps_digital: "/portal/servicos/aps-digital",
  aps_fisica: "/portal/servicos/aps-fisica",
  insights_ia: "/portal/salvia",
} as const;

const BASE_APS_SECTIONS: PortalServiceSection[] = [
  { id: "visao-geral", label: "Visão geral", description: "Resumo executivo do módulo para o contexto do cliente." },
  { id: "populacao", label: "Pirâmide etária", description: "Leitura demográfica e composição da base elegível." },
  { id: "producao", label: "Indicadores de produção", description: "Produção, acesso, conversão e volume assistencial." },
  { id: "experiencia", label: "Indicadores de eficiência", description: "Experiência, eficiência operacional e leitura qualitativa." },
  { id: "saude-populacional", label: "Gestão do cuidado", description: "Risco, rastreamentos, condições de saúde e coordenação do cuidado." },
  { id: "acoes-coletivas", label: "Ações de saúde", description: "Mobilização, engajamento e ações coletivas do módulo." },
  { id: "busca-ativa", label: "Busca ativa", description: "Campanhas, segmentos e conversão da busca ativa." },
  { id: "monitoramento", label: "Busca ativa analítico", description: "Leitura analítica de monitoramento, eventos e continuidade do cuidado." },
];

const TELEMEDICINA_SECTIONS: PortalServiceSection[] = [
  { id: "visao-geral", label: "Visão geral", description: "Resumo executivo de volume, penetração e experiência." },
  { id: "producao", label: "Indicadores de produção médica", description: "Volume atendido, especialidades e ritmo assistencial." },
  { id: "eficiencia", label: "Indicadores de produção enf.", description: "Fila, eficiência operacional e capacidade do cuidado." },
  { id: "no-show", label: "No-show", description: "Perdas de agenda, aderência e oportunidade de conversão." },
];

const APS_ADMIN_SECTION: PortalServiceSection = {
  id: "avaliacao-global",
  label: "Avaliação global",
  description: "Leitura consolidada exclusiva para administração global.",
  adminOnly: true,
};

const PORTAL_SERVICE_SECTION_MAP: Record<string, PortalServiceSection[]> = {
  telemedicina: TELEMEDICINA_SECTIONS,
  aps_fisica: [APS_ADMIN_SECTION, ...BASE_APS_SECTIONS],
  aps_digital: [APS_ADMIN_SECTION, ...BASE_APS_SECTIONS],
};

const PORTAL_HOME_ROUTES = [
  "/portal",
  "/dashboard",
  "/portal/minha-conta",
  "/portal/treinamentos",
  "/portal/base-de-pessoas",
  ...Object.values(PORTAL_SERVICE_ROUTE_MAP),
];

const NUCLEUS_ROUTES = [
  "/modulos/nucleo",
  "/channels",
  "/clients",
  "/contratos",
  "/servicos",
  "/configuracoes/usuarios",
  "/usuarios",
];

const NR1_ROUTES = [
  "/modulos/nr1",
  "/home/salvia",
  "/home/canal",
  "/home/rh",
  "/dashboards",
  "/dashboards/salvia",
  "/dashboards/canal",
  "/dashboards/consultor",
  "/dashboards/rh",
  "/importacao",
  "/questionarios",
  "/meus-questionarios",
  "/evaluations",
  "/meus-resultados",
  "/documents",
  "/motor-analise",
  "/sistema/erros",
];

export function resolvePortalEntryRoute() {
  return "/portal";
}

export function resolvePortalAccountRoute() {
  return "/portal/minha-conta";
}

export function resolvePortalServiceRoute(moduleKey?: string | null) {
  if (!moduleKey) {
    return "/portal";
  }

  return PORTAL_SERVICE_ROUTE_MAP[moduleKey as keyof typeof PORTAL_SERVICE_ROUTE_MAP] ?? "/portal";
}

export function resolvePortalServiceSectionStorageKey(moduleKey?: string | null) {
  return `pjc.portal.active-section.${moduleKey ?? "default"}`;
}

export function getPortalServiceSections(moduleKey?: string | null, role?: string | null) {
  const sections = PORTAL_SERVICE_SECTION_MAP[moduleKey ?? ""] ?? [];

  return sections.filter((section) => {
    if (!section.adminOnly) return true;
    return role === "admin" || role === "salvia_operation_admin";
  });
}

export function buildPortalServiceNavigationItem(params: {
  moduleKey?: string | null;
  serviceName?: string | null;
  role?: string | null;
}) {
  const href = resolvePortalServiceRoute(params.moduleKey);
  const sections = getPortalServiceSections(params.moduleKey, params.role);

  return {
    label: params.serviceName || params.moduleKey?.toUpperCase() || "Serviço",
    href,
    moduleKey: params.moduleKey ?? undefined,
    children: sections.map((section) => ({
      label: section.label,
      href,
      sectionId: section.id,
      moduleKey: params.moduleKey ?? undefined,
    })),
  } satisfies NavigationTreeItem;
}

// PJC-95: Visão Geral do NR1 e Dashboards seguem em desenvolvimento e ficam restritos ao admin estrutural.
export function canViewNr1OverviewAndDashboards(role?: string | null) {
  return role === "admin" || role === "salvia_operation_admin";
}

// PJC-95: sem Visão Geral e Dashboards, o consultor de canal não tem nenhum item no NR1.
export function isNr1HiddenForRole(role?: string | null) {
  return role === "channel_consultant";
}

export function resolveNr1OverviewRoute(role?: string | null) {
  if (canViewNr1OverviewAndDashboards(role)) {
    return "/home/salvia";
  }

  if (role === "client_rh") {
    return "/importacao";
  }

  if (role === "channel_admin" || role === "channel_admin_multi" || role === "channel_operator") {
    return "/questionarios";
  }

  return "/portal";
}

export function resolveNr1DashboardHubRoute(role?: string | null) {
  const roleDashboardMap: Record<string, string> = {
    admin: "/dashboards/salvia",
    salvia_operation_admin: "/dashboards/salvia",
    channel_admin: "/dashboards/canal",
    channel_admin_multi: "/dashboards/canal",
    channel_operator: "/dashboards/canal",
    channel_consultant: "/dashboards/consultor",
    client_rh: "/dashboards/rh",
  };

  return role ? (roleDashboardMap[role] || "/dashboards/rh") : "/dashboards/rh";
}

export function resolvePortalContext(location: string): PortalContext {
  if (PORTAL_HOME_ROUTES.some((route) => location === route || location.startsWith(`${route}/`))) {
    return "portal";
  }

  if (NUCLEUS_ROUTES.some((route) => location === route || location.startsWith(`${route}/`))) {
    return "nucleo";
  }

  if (NR1_ROUTES.some((route) => location === route || location.startsWith(`${route}/`))) {
    return "nr1";
  }

  return "portal";
}

export const NR1_SIDEBAR_ALLOWED_LABELS = [
  "NR1",
  "Visão Geral",
  "Visão Geral do NR1",
  "Dashboards",
  "Estrutura do NR1",
  "Base de Setores e Cargos",
  "Clientes",
  "Operação",
  "Importação",
  "Questionários",
  "Resultados",
  "Avaliações",
  "Documentos",
  "Avançado",
  "Motor de Análise",
  "Sistema",
] as const;

export function getModuleAccessByKey(modules: PortalShellModuleAccess[], moduleKey: string) {
  return modules.find((item) => item.moduleKey === moduleKey) || null;
}

export function hasVisibleModuleAccess(moduleAccess?: PortalShellModuleAccess | null) {
  return Boolean(
    moduleAccess?.userHasVisibleAccess ||
      moduleAccess?.userHasOperationalAccess ||
      moduleAccess?.visibilityState === "visible" ||
      moduleAccess?.visibilityState === "operable",
  );
}

export function hasAnyVisibleModuleAccess(modules: PortalShellModuleAccess[], moduleKeys: string[]) {
  return moduleKeys.some((moduleKey) => hasVisibleModuleAccess(getModuleAccessByKey(modules, moduleKey)));
}

export function resolveSidebarShellMode(params: {
  location: string;
  isNr1RouteBlocked: boolean;
}) {
  if (params.isNr1RouteBlocked) {
    return "blocked";
  }

  return resolvePortalContext(params.location);
}

export function filterNr1SidebarItems<T extends { label: string }>(items: T[]) {
  const hasContextualSystemGroup = items.some((item) => item.label === "Sistema");

  return items.filter((item) => {
    if (item.label === "Clientes" && !hasContextualSystemGroup) {
      return false;
    }

    return NR1_SIDEBAR_ALLOWED_LABELS.includes(item.label as (typeof NR1_SIDEBAR_ALLOWED_LABELS)[number]);
  });
}

export function isModuleRouteBlocked(params: {
  location: string;
  moduleAccess: PortalShellModuleAccess | null;
}) {
  const context = resolvePortalContext(params.location);
  const isPortalRoute = context === "portal" || context === "nucleo";

  return !isPortalRoute && !!params.moduleAccess && params.moduleAccess.userHasOperationalAccess === false;
}

export function countAccessibleModules(modules: PortalShellModuleAccess[]) {
  return modules.filter((item) => item.userHasOperationalAccess).length;
}

export function countVisibleModules(modules: PortalShellModuleAccess[]) {
  return modules.filter((item) => item.userHasVisibleAccess || item.visibilityState === "visible" || item.visibilityState === "operable").length;
}

export function countContractedServices(services: PortalShellService[], modules?: PortalShellModuleAccess[]) {
  const contractedCount = services.filter((item) => item.companyHasService).length;

  if (!modules) {
    return contractedCount;
  }

  return contractedCount + modules.filter((item) => item.userHasOperationalAccess).length;
}

export function countCommerciallyEnabledServices(services: PortalShellService[]) {
  return services.filter((item) => item.channelHasService).length;
}

function buildNavigationKey(path: string[]) {
  return path.join("::");
}

function buildNavigationBranchKeys(path: string[]) {
  return path.map((_, index) => buildNavigationKey(path.slice(0, index + 1)));
}

export function collectExpandedMenuKeys(location: string, items: NavigationTreeItem[], parentPath: string[] = []): string[] {
  for (const item of items) {
    const currentPath = [...parentPath, item.label];
    const nestedKeys = item.children?.length
      ? collectExpandedMenuKeys(location, item.children, currentPath)
      : [];

    if ((item.href && location === item.href) || nestedKeys.length > 0) {
      return Array.from(new Set([...buildNavigationBranchKeys(currentPath), ...nestedKeys]));
    }
  }

  return [];
}
